import { useMemo, useState } from "react";

const API_BASE =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

function extractErrorMessage(payload, fallback) {
  const detail = payload?.detail ?? payload?.error ?? payload?.message;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") {
      if (typeof first.msg === "string") return first.msg;
      if (typeof first.message === "string") return first.message;
    }
  }

  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    return JSON.stringify(detail);
  }

  return fallback;
}

function fromCaughtError(err, fallback) {
  if (err instanceof Error && err.message && err.message !== "[object Object]") {
    return err.message;
  }

  if (typeof err === "string" && err.trim()) {
    return err;
  }

  if (err && typeof err === "object") {
    return extractErrorMessage(err.response?.data || err, fallback);
  }

  return fallback;
}
const RELOCATION_OPTIONS = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Northern Cape",
  "Western Cape",
  "Any Province",
  "No, not willing to relocate",
];
const PROVINCE_OPTIONS = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Northern Cape",
  "Western Cape",
];
const LICENSE_TYPE_OPTIONS = [
  "Code B",
  "Code C1",
  "Code C",
  "Code EB",
  "Code EC1",
  "Code EC",
  "Learners License",
];

function createEmptyReference() {
  return {
    referenceName: "",
    referenceEmail: "",
    referenceContactDetails: "",
    periodWorkedWithCompany: "",
  };
}

function UploadStatus({ file, required }) {
  const text = file
    ? "✓ Uploaded"
    : required
    ? "Required"
    : "Optional";

  const color = file ? "#137333" : required ? "#b3261e" : "#555";

  return <span style={{ color, fontSize: 13, marginLeft: 8 }}>{text}</span>;
}

function NewEmployee() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    applicantRole: "Optometrist",
    practitionerNumber: "",
    salaryExpectation: "",
    isPreviousEmployee: false,
    separationReason: "",
    contactDetails: "",
    streetAddress: "",
    suburb: "",
    currentProvince: "",
    postalCode: "",
    availabilityStatus: "available",
    availableFrom: "",
    availabilityNotes: "",
    hasDriversLicense: "no",
    driversLicenseInProgress: "no",
    driversLicenseProcessNote: "",
    driversLicenseType: "",
    driversLicenseValidFrom: "",
    driversLicenseExpiry: "",
    driversLicenseIssuedCountry: "South Africa",
    driversLicenseFirstIssued: "",
    hasDegreeCopy: "no",
    degreeReason: "",
  });
  const [relocationPreferences, setRelocationPreferences] = useState([]);
  const [references, setReferences] = useState([createEmptyReference()]);

  const [files, setFiles] = useState({
    hpcsaCertificate: null,
    cpdComplianceCertificate: null,
    idDocument: null,
    degreeCopyFile: null,
    cvFile: null,
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const requiredOk = useMemo(() => {
    return (
      !!files.hpcsaCertificate &&
      !!files.cpdComplianceCertificate &&
      !!files.idDocument
    );
  }, [files]);

  const conditionalOk = useMemo(() => {
    const driversOk =
      form.hasDriversLicense === "no"
        ? ["yes", "no"].includes(form.driversLicenseInProgress)
        : !!form.driversLicenseType &&
          !!form.driversLicenseValidFrom &&
          !!form.driversLicenseExpiry &&
          !!form.driversLicenseIssuedCountry.trim() &&
          !!form.driversLicenseFirstIssued;

    const degreeOk =
      form.hasDegreeCopy === "yes"
        ? !!files.degreeCopyFile
        : form.degreeReason.trim().length > 0;

    const availabilityOk =
      form.availabilityStatus === "available" ||
      (form.availabilityStatus === "unavailable" && !!form.availableFrom);

    return driversOk && degreeOk && availabilityOk;
  }, [files, form]);

  const canSubmit =
    form.name.trim() &&
    form.email.trim() &&
    form.applicantRole.trim() &&
    form.practitionerNumber.trim() &&
    form.salaryExpectation.trim() &&
    form.contactDetails.trim() &&
    form.streetAddress.trim() &&
    form.suburb.trim() &&
    form.currentProvince.trim() &&
    form.postalCode.trim() &&
    references.length > 0 &&
    references.every(
      (reference) =>
        reference.referenceName.trim() &&
        reference.referenceEmail.trim() &&
        reference.referenceContactDetails.trim() &&
        reference.periodWorkedWithCompany.trim()
    ) &&
    requiredOk &&
    conditionalOk;

  const onTextChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onFileChange = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file || null }));
  };

  const onRelocationToggle = (option) => {
    setRelocationPreferences((prev) => {
      const exists = prev.includes(option);

      if (option === "Any Province" || option === "No, not willing to relocate") {
        return exists ? [] : [option];
      }

      const nextValues = exists
        ? prev.filter((value) => value !== option)
        : [
            ...prev.filter(
              (value) => value !== "Any Province" && value !== "No, not willing to relocate"
            ),
            option,
          ];

      return nextValues;
    });
  };

  const onReferenceChange = (index, field, value) => {
    setReferences((prev) =>
      prev.map((reference, currentIndex) =>
        currentIndex === index ? { ...reference, [field]: value } : reference
      )
    );
  };

  const addReference = () => {
    setReferences((prev) => [...prev, createEmptyReference()]);
  };

  const removeReference = (index) => {
    setReferences((prev) => (prev.length === 1 ? prev : prev.filter((_, currentIndex) => currentIndex !== index)));
  };

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      applicantRole: "Optometrist",
      practitionerNumber: "",
      salaryExpectation: "",
      contactDetails: "",
      streetAddress: "",
      suburb: "",
      currentProvince: "",
      postalCode: "",
      availabilityStatus: "available",
      availableFrom: "",
      availabilityNotes: "",
      hasDriversLicense: "no",
      driversLicenseInProgress: "no",
      driversLicenseProcessNote: "",
      driversLicenseType: "",
      driversLicenseValidFrom: "",
      driversLicenseExpiry: "",
      driversLicenseIssuedCountry: "South Africa",
      driversLicenseFirstIssued: "",
      hasDegreeCopy: "no",
      degreeReason: "",
    });
    setRelocationPreferences([]);
    setReferences([createEmptyReference()]);
    setFiles({
      hpcsaCertificate: null,
      cpdComplianceCertificate: null,
      idDocument: null,
      degreeCopyFile: null,
      cvFile: null,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess(null);

    if (!canSubmit) {
      setError("Please complete all required fields and required uploads.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("email", form.email.trim());
      // password is auto-generated by the server and emailed to the applicant
      payload.append("applicant_role", form.applicantRole);
      payload.append("practitioner_number", form.practitionerNumber.trim());
      payload.append("salary_expectation", form.salaryExpectation.trim());
      payload.append("is_previous_employee", form.isPreviousEmployee);
      payload.append("separation_reason", form.separationReason);
      payload.append("contact_details", form.contactDetails.trim());
      payload.append(
        "references_json",
        JSON.stringify(
          references.map((reference) => ({
            reference_name: reference.referenceName.trim(),
            reference_email: reference.referenceEmail.trim(),
            reference_contact_details: reference.referenceContactDetails.trim(),
            period_worked_with_company: reference.periodWorkedWithCompany.trim(),
          }))
        )
      );
      payload.append("street_address", form.streetAddress.trim());
      payload.append("suburb", form.suburb.trim());
      payload.append("current_province", form.currentProvince.trim());
      payload.append("postal_code", form.postalCode.trim());
      payload.append("relocation_preferences", relocationPreferences.join("|"));
      payload.append("availability_status", form.availabilityStatus);
      payload.append("available_from", form.availableFrom);
      payload.append("availability_notes", form.availabilityNotes.trim());
      payload.append("has_drivers_license", form.hasDriversLicense);
      payload.append("drivers_license_in_progress", form.driversLicenseInProgress);
      payload.append("drivers_license_process_note", form.driversLicenseProcessNote);
      payload.append("drivers_license_type", form.driversLicenseType);
      payload.append("drivers_license_valid_from", form.driversLicenseValidFrom);
      payload.append("drivers_license_expiry", form.driversLicenseExpiry);
      payload.append("drivers_license_issued_country", form.driversLicenseIssuedCountry.trim());
      payload.append("drivers_license_first_issued", form.driversLicenseFirstIssued);
      payload.append("has_degree_copy", form.hasDegreeCopy);
      payload.append("degree_reason", form.degreeReason.trim());

      payload.append("hpcsa_certificate", files.hpcsaCertificate);
      payload.append("cpd_compliance_certificate", files.cpdComplianceCertificate);
      payload.append("id_document", files.idDocument);

      if (files.degreeCopyFile) {
        payload.append("degree_copy_file", files.degreeCopyFile);
      }

      if (files.cvFile) {
        payload.append("cv_file", files.cvFile);
      }

      const response = await fetch(`${API_BASE}/api/auth/register/employee`, {
        method: "POST",
        body: payload,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, "Registration failed"));
      }

      setSuccess(data);
      resetForm();
    } catch (submitError) {
      setError(fromCaughtError(submitError, "Registration failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: 760,
        border: "1px solid #d9d9d9",
        borderRadius: 12,
        padding: 18,
        background: "#fff",
      }}
    >
      <h2 style={{ marginTop: 0 }}>Employee Applicant Registration</h2>
      <p style={{ color: "#444" }}>
        Required documents: HPCSA certificate, CPD compliance certificate, and ID.
        Registration is blocked until these are uploaded.
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: "grid", gap: 10 }}>
          <input
            type="text"
            name="name"
            placeholder="Full Name and Surname"
            value={form.name}
            onChange={onTextChange}
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={onTextChange}
            required
          />

          <select
            name="applicantRole"
            value={form.applicantRole}
            onChange={onTextChange}
            required
          >
            <option value="Optometrist">Optometrist</option>
            <option value="Optical Dispenser">Optical Dispenser</option>
          </select>

          <input
            type="text"
            name="practitionerNumber"
            placeholder="HPCSA OP / Practitioner Number"
            value={form.practitionerNumber}
            onChange={onTextChange}
            required
          />

          <input
            type="text"
            name="salaryExpectation"
            placeholder="Salary Expectations"
            value={form.salaryExpectation}
            onChange={onTextChange}
            required
          />

          <div
            style={{
              background: "#e6f3ff",
              border: "1px solid #b3d9ff",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <strong>Employment History</strong>
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.isPreviousEmployee}
                  onChange={(e) => setForm({...form, isPreviousEmployee: e.target.checked, separationReason: ""})}
                />
                Previous Employee
              </label>

              {form.isPreviousEmployee && (
                <select
                  name="separationReason"
                  value={form.separationReason}
                  onChange={onTextChange}
                  required
                  style={{ marginTop: 8 }}
                >
                  <option value="">How did they leave previously?</option>
                  <option value="retrenched">Retrenched</option>
                  <option value="resigned">Resigned</option>
                </select>
              )}
            </div>
          </div>

          <div
            style={{
              background: "#f7fafc",
              border: "1px solid #d7e3f0",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <strong>Contact Details</strong>
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <input
                type="text"
                name="contactDetails"
                placeholder="Contact details (phone, email, etc.)"
                value={form.contactDetails}
                onChange={onTextChange}
                required
              />
            </div>
          </div>

          <div
            style={{
              background: "#f7fafc",
              border: "1px solid #d7e3f0",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <strong>Current location</strong>
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <input
                type="text"
                name="streetAddress"
                placeholder="Street address"
                value={form.streetAddress}
                onChange={onTextChange}
                required
              />
              <input
                type="text"
                name="suburb"
                placeholder="Suburb"
                value={form.suburb}
                onChange={onTextChange}
                required
              />
              <select
                name="currentProvince"
                value={form.currentProvince}
                onChange={onTextChange}
                required
              >
                <option value="">Select current province</option>
                {PROVINCE_OPTIONS.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="postalCode"
                placeholder="Postal Code"
                value={form.postalCode}
                onChange={onTextChange}
                required
              />
            </div>
          </div>

          <div
            style={{
              background: "#f7fafc",
              border: "1px solid #d7e3f0",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <strong>Availability</strong>
            <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
              <select
                name="availabilityStatus"
                value={form.availabilityStatus}
                onChange={onTextChange}
              >
                <option value="available">Available immediately</option>
                <option value="unavailable">Currently unavailable</option>
              </select>

              {form.availabilityStatus === "unavailable" && (
                <>
                  <label>Available from date</label>
                  <input
                    type="date"
                    name="availableFrom"
                    value={form.availableFrom}
                    onChange={onTextChange}
                    required
                  />
                </>
              )}

              <label>Notice period</label>
              <textarea
                name="availabilityNotes"
                rows={3}
                value={form.availabilityNotes}
                onChange={onTextChange}
                placeholder="Notice period"
              />
            </div>
          </div>

          <div
            style={{
              background: "#f7fafc",
              border: "1px solid #d7e3f0",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <strong>References</strong>
              <button type="button" onClick={addReference} style={{ padding: "8px 12px" }}>
                Add another reference
              </button>
            </div>
            <div style={{ display: "grid", gap: 12, marginTop: 10 }}>
              {references.map((reference, index) => (
                <div
                  key={`reference-${index}`}
                  style={{
                    border: "1px solid #d7e3f0",
                    borderRadius: 8,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                    background: "#fff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <strong>{`Reference ${index + 1}`}</strong>
                    {references.length > 1 ? (
                      <button type="button" onClick={() => removeReference(index)} style={{ padding: "6px 10px" }}>
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <input
                    type="text"
                    placeholder="Reference name and surname"
                    value={reference.referenceName}
                    onChange={(event) => onReferenceChange(index, "referenceName", event.target.value)}
                    required
                  />
                  <input
                    type="email"
                    placeholder="Reference email"
                    value={reference.referenceEmail}
                    onChange={(event) => onReferenceChange(index, "referenceEmail", event.target.value)}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Reference contact details"
                    value={reference.referenceContactDetails}
                    onChange={(event) => onReferenceChange(index, "referenceContactDetails", event.target.value)}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Period worked with the company"
                    value={reference.periodWorkedWithCompany}
                    onChange={(event) => onReferenceChange(index, "periodWorkedWithCompany", event.target.value)}
                    required
                  />
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              background: "#f7fafc",
              border: "1px solid #d7e3f0",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <strong>Willing to relocate to</strong>
            <p style={{ margin: "8px 0 10px", color: "#444" }}>
              Select the provinces or regions where you are willing to relocate.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
              {RELOCATION_OPTIONS.map((option) => (
                <label key={option} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={relocationPreferences.includes(option)}
                    onChange={() => onRelocationToggle(option)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            <div style={{ marginTop: 10, color: "#35506b", fontSize: 14 }}>
              Selected: {relocationPreferences.length > 0 ? relocationPreferences.join(", ") : "No relocation preferences selected"}
            </div>
          </div>

          <label>
            HPCSA certificate
            <UploadStatus file={files.hpcsaCertificate} required />
          </label>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) =>
              onFileChange("hpcsaCertificate", e.target.files?.[0] || null)
            }
            required
          />

          <label>
            CPD compliance certificate
            <UploadStatus file={files.cpdComplianceCertificate} required />
          </label>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) =>
              onFileChange(
                "cpdComplianceCertificate",
                e.target.files?.[0] || null
              )
            }
            required
          />

          <label>
            ID document
            <UploadStatus file={files.idDocument} required />
          </label>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => onFileChange("idDocument", e.target.files?.[0] || null)}
            required
          />

          <label>
            Curriculum Vitae (CV)
            <UploadStatus file={files.cvFile} required={false} />
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => onFileChange("cvFile", e.target.files?.[0] || null)}
          />

          <label>Do you have a driver's license?</label>
          <select
            name="hasDriversLicense"
            value={form.hasDriversLicense}
            onChange={onTextChange}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>

          {form.hasDriversLicense === "yes" ? (
            <>
              <label>Type of license</label>
              <select
                name="driversLicenseType"
                value={form.driversLicenseType}
                onChange={onTextChange}
                required
              >
                <option value="">Select license type</option>
                {LICENSE_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <label>Valid from</label>
              <input
                type="date"
                name="driversLicenseValidFrom"
                value={form.driversLicenseValidFrom}
                onChange={onTextChange}
                required
              />

              <label>Expiry date</label>
              <input
                type="date"
                name="driversLicenseExpiry"
                value={form.driversLicenseExpiry}
                onChange={onTextChange}
                required
              />

              <label>Country where license was issued</label>
              <input
                type="text"
                name="driversLicenseIssuedCountry"
                value={form.driversLicenseIssuedCountry}
                onChange={onTextChange}
                required
              />

              <label>First issued date</label>
              <input
                type="date"
                name="driversLicenseFirstIssued"
                value={form.driversLicenseFirstIssued}
                onChange={onTextChange}
                required
              />
            </>
          ) : (
            <>
              <label>Are you in the process of getting a driver's license?</label>
              <select
                name="driversLicenseInProgress"
                value={form.driversLicenseInProgress}
                onChange={onTextChange}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
              {form.driversLicenseInProgress === "yes" && (
                <>
                  <label>Where in the process are you?</label>
                  <textarea
                    name="driversLicenseProcessNote"
                    value={form.driversLicenseProcessNote}
                    onChange={onTextChange}
                    placeholder="Describe where you are in the process of getting your license"
                    rows={3}
                    style={{ width: "100%", resize: "vertical" }}
                  />
                </>
              )}
            </>
          )}

          <label>Do you have a copy of your degree?</label>
          <select
            name="hasDegreeCopy"
            value={form.hasDegreeCopy}
            onChange={onTextChange}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>

          {form.hasDegreeCopy === "yes" ? (
            <>
              <label>
                Upload degree copy
                <UploadStatus file={files.degreeCopyFile} required />
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) =>
                  onFileChange("degreeCopyFile", e.target.files?.[0] || null)
                }
                required
              />
            </>
          ) : (
            <>
              <label>Reason for not having a degree copy</label>
              <textarea
                name="degreeReason"
                rows={3}
                value={form.degreeReason}
                onChange={onTextChange}
                placeholder="Please explain why you do not have a degree copy"
                required
              />
            </>
          )}

          <div
            style={{
              background: "#f7fafc",
              border: "1px solid #d7e3f0",
              borderRadius: 8,
              padding: 10,
            }}
          >
            <strong>Checklist</strong>
            <div>HPCSA: {files.hpcsaCertificate ? "✓" : "✗"}</div>
            <div>CPD Compliance: {files.cpdComplianceCertificate ? "✓" : "✗"}</div>
            <div>ID Document: {files.idDocument ? "✓" : "✗"}</div>
          </div>

          {error && <div style={{ color: "#b3261e" }}>{error}</div>}
          {success && (
            <div style={{ color: "#137333" }}>
              {success.message} Ticket nr: <strong>{success.folder_number}</strong>
              {" | "}Profession: <strong>{success.applicant_role || "N/A"}</strong>
              {" | "}HPCSA: <strong>{success.hpcsa_verification?.status || "VERIFIED"}</strong>
              {" | "}Review status: <strong>{success.application_review_status || "Under review"}</strong>
              {" | "}Current employer: <strong>{success.availability?.current_employer || "N/A"}</strong>
              {" | "}Login username: <strong>{success.user?.email || success.contact_details}</strong>
              {success.relocation_preferences?.length ? (
                <>
                  {" | "}Relocation: <strong>{success.relocation_preferences.join(", ")}</strong>
                </>
              ) : null}
              {success.current_location?.suburb ? (
                <>
                  {" | "}Location: <strong>{success.current_location.street_address}, {success.current_location.suburb}, {success.current_location.province}, {success.current_location.postal_code}</strong>
                </>
              ) : null}
              {success.email_warning ? (
                <>
                  {" | "}Email notice: <strong>{success.email_warning}</strong>
                </>
              ) : (
                <>
                  {" | "}Confirmation email sent to <strong>{success.user?.email}</strong>
                </>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit || submitting}
            style={{
              width: "fit-content",
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #1f4ea3",
              background: !canSubmit || submitting ? "#aec4e8" : "#1f4ea3",
              color: "#fff",
              cursor: !canSubmit || submitting ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Submitting..." : "Register as Employee Applicant"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NewEmployee;
